/* global globalThis */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ConfirmButton from "../ConfirmButton/ConfirmButton";
import { reactGaEvent } from "examsbook-react-sdk/actions/Common";
import { useStyles, useTranslations } from "examsbook-react-sdk/custom-hooks";
import { useHistory } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import CommonButton from '../../../constants/CommonButton';
import { useTheme } from '../../../constants/themeProvider';
import { Box, Typography, CircularProgress } from '@material-ui/core'; // Import CircularProgress
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import { getQuizStatus } from "examsbook-react-sdk/common";

function ConfirmAlertUI({ onConfirm, onClose, t, classes, themePrimaryColor }) {
  return (
    <Box className={classes.popupBox}>
      <Typography variant="h2" className={classes.popupText}>{t("Are you sure to quit this quiz")}</Typography>
      <Box style={{ display: 'flex', flexDirection: 'row' }}>
        <button
          type="button"
          onClick={onConfirm}
          className={classes.yesButton}
          style={{ backgroundColor: themePrimaryColor }}
        >
          {t('Yes')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className={classes.noButton}
          style={{ backgroundColor: themePrimaryColor }}
        >
          {t('No')}
        </button>
      </Box>
    </Box>
  );
}

ConfirmAlertUI.propTypes = {
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  t: PropTypes.func,
  classes: PropTypes.object,
  themePrimaryColor: PropTypes.string,
};

function ResultModal({
  showModal,
  closeModal,
  classes,
  isLoadingQuizResponse,
  t,
  hasQuizSubmissionError,
  onComplete
}) {
  if (showModal === false) {
    return null;
  }

  return (
    <Box
      className={classes.modalBg + " show"}
      onClick={closeModal}
    >
      <Box
        className={classes.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        {isLoadingQuizResponse ? (
          <Box className={classes.heading} sx={{ p: 2 }}>
            <CircularProgress style={{ color: '#0C3450' }} />
            <Typography variant="h2" className={classes.headingText}>
              {t("Please wait, your result is getting prepared")}
            </Typography>
          </Box>
        ) : (
          <Box className={classes.heading}>
            {hasQuizSubmissionError ? (
              <Typography variant="h2" className={classes.headingText}>
                {t("There was an issue while submitting the quiz.")}
              </Typography>
            ) : (
              <Typography variant="h2" className={classes.headingText}>
                {t("Your quiz is submitted successfully!")}
              </Typography>
            )}
            <Box className={classes.okAlign}>
              <CommonButton
                onClick={onComplete}
                classStyleHeading={classes.okBtn}
                title={hasQuizSubmissionError ? t("Go to Dashboard") : t("Ok")}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

ResultModal.propTypes = {
  showModal: PropTypes.bool,
  closeModal: PropTypes.func,
  classes: PropTypes.object,
  isLoadingQuizResponse: PropTypes.bool,
  t: PropTypes.func,
  hasQuizSubmissionError: PropTypes.bool,
  onComplete: PropTypes.func,
};

function createConfirmAlertCustomUI({ onConfirmLeave, t, classes, themePrimaryColor }) {
  return (confirmProps) => React.createElement(ConfirmAlertUI, {
    onClose: confirmProps.onClose,
    onConfirm: () => onConfirmLeave(confirmProps.onClose),
    t,
    classes,
    themePrimaryColor
  });
}

/**
 * SubmitButton button.
 * @param props
 * @returns {string}
 * @constructor
 */
function SubmitButton(props) {
    const { mode, viewMode, hideSubmitButton, slug, submitQuiz, singleCourseId } = props;
    const t = useTranslations();
    const location = useLocation();
    const showEvaluationTest = useSelector(state => state.filteredCourseList.showEvaluationTest) || location?.state?.showEvaluationTest;
    const showInstructions = useSelector(state => state.filteredCourseList.showInstructions) || location?.state?.showInstructions;
    const classes = useStyles(styles)();
    const history = useHistory();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const testData = useSelector(state => state.filteredCourseList.testData);
    const courseName = useSelector(state => state.filteredCourseList.courseName);
    const courseIdFromRedux = useSelector(state => state.filteredCourseList.courseId);
    const courseId = courseIdFromRedux ?? singleCourseId;
    const courseProgress = useSelector(state => state.filteredCourseList.courseProgress || 0);
    const theme = useTheme();
    const quizResultData = useSelector((state) => state.getQuizResultReducer.quizResult);
    const isLoadingQuizResponse = useSelector((state) => state.getQuizResultReducer.isLoading);
    const quiz = useSelector((state) => state.quiz);
    const { notVisited } = getQuizStatus(quiz.questions);
    const allQuestionsVisited = notVisited === 0;
    const hasQuizSubmissionError = Boolean(quizResultData?.error);
    const quizPath = globalThis?.location?.pathname || '';

    useEffect(() => {
      if (isModalVisible) {
        localStorage.setItem("quizCompleted", "true");
        localStorage.setItem("quizCompletedPath", quizPath);
        return;
      }

      localStorage.removeItem("quizCompleted");
      localStorage.removeItem("quizCompletedPath");
    }, [isModalVisible, quizPath]);

    useEffect(() => {
      const onConfirmLeave = async (onClose) => {
        onClose();
        await submitQuiz();
        history.push(`/reports/${slug}/${courseId}`, {
          courseId: courseId,
          slug: slug,
          pageType: "afterTestResult",
          params: globalThis.location.pathname,
          courseName: courseName
        });
        localStorage.removeItem('quizCompleted');
        localStorage.removeItem('quizCompletedPath');
      };

      const customUI = createConfirmAlertCustomUI({
        onConfirmLeave,
        t,
        classes,
        themePrimaryColor: theme?.primaryColor
      });

      const handlePopState = () => {
        const isQuizRoute = globalThis.location.pathname.includes('/quiz');
        const quizCompleted = localStorage.getItem('quizCompleted');
        if (isQuizRoute && viewMode === false && !quizCompleted) {
          confirmAlert({ customUI });
          history.replace('/my-courses');
          history.push(`/course/${courseId}`, {
            courseId: courseId,
            subjectId: '',
            topicId: '',
            subjectDetail: testData,
            courseName: courseName,
            subjectName: '',
            topicName: '',
            showEvaluationTest: showEvaluationTest,
            showInstructions: showInstructions,
            courseProgress: courseProgress
          });
        }
      };

      globalThis.history.pushState(null, '', globalThis.location.href);
      globalThis.addEventListener('popstate', handlePopState);
      return () => globalThis.removeEventListener('popstate', handlePopState);
    }, [
      classes,
      courseId,
      courseName,
      courseProgress,
      history,
      showEvaluationTest,
      showInstructions,
      slug,
      submitQuiz,
      t,
      testData,
      theme?.primaryColor,
      viewMode
    ]);

    const handleClickOnYes = async () => {
        openModal();
      await submitQuiz();
        reactGaEvent("quiz", "Confirm Submit");
    };

    const handleClickOnNo = () => {
        reactGaEvent("quiz", "Not Confirm For Submit");
    };

    const openModal = () => {
        setIsModalVisible(true);
      };
    
      const closeModal = () => {
        setIsModalVisible(false);
      };

    const handleModalComplete = () => {
        history.replace("/my-courses", {
            params: globalThis.location.pathname,
        });

        if (hasQuizSubmissionError) {
            history.push("/dashboard");
        } else {
            history.push(`/reports/${slug}/${courseId}`, {
                courseId: courseId,
                slug: slug,
                pageType: "afterTestResult",
                params: globalThis.location.pathname,
                courseName: courseName,
            });
        }

        localStorage.removeItem("quizCompleted");
    localStorage.removeItem("quizCompletedPath");
    };

    return (
        <React.Fragment>
        {viewMode ? null :
                <Box className={(hideSubmitButton ? classes.hideOnDesktop : '') + ' ' + classes.submit}>
                    <ConfirmButton mode={mode} text={t("Submit")}
                                   clickOnYes={handleClickOnYes}
                                   clickOnNo={handleClickOnNo}
                                   disabled={!allQuestionsVisited}/>
                </Box>}
        <ResultModal
          showModal={isModalVisible}
          closeModal={closeModal}
          classes={classes}
          isLoadingQuizResponse={isLoadingQuizResponse}
          t={t}
          hasQuizSubmissionError={hasQuizSubmissionError}
          onComplete={handleModalComplete}
        />
        </React.Fragment>
    );
}

const styles = theme => ({
    submit: {
        float: 'right',
        lineHeight: '33px',
        marginRight: 5,
        color: '#fff',
        '& button': {
            display: 'inline-block',
            padding: '14px 30px',
            borderRadius: 3,
            color: '#fff',
            fontSize: '18px',
            fontWeight: 500,
            backgroundColor: '#0C3450',
            lineHeight: '10px',
            height: 'auto',
            border: 0,
            cursor: 'pointer',
            textAlign: 'center',
            position: 'relative',
            boxShadow: '4px 4px #88888838',
            '&:hover': {
                background: '#0c3450e0 !important',
                color:'#fff',
                '@media (max-width: 767px)': {
                    background: '#0C3450',
                }
            }
        },
        '@media (max-width:767px)': {
            width: 'auto',
            '& button': {
                padding: '10px 15px',
            }
        }
    },
    hideOnDesktop: {
        '@media (max-width:767px)': {
            display: 'inline-block',
        }
    },
    modalBg: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
    },   
    modalContent: {
        width: '460px',
        margin: 'auto',
        marginTop: '40vh',
        '@media (max-width:439px)': {
            width: '280px'
        }
    },
    heading: {
        display:'flex',
        flexDirection:'column',
        alignItems:'center',
        justifyContent:'center',
        backgroundColor:'#fff',
        padding:'20px 40px',
        borderRadius: '6px'
    },
    headingText: {
        fontWeight:'normal',
        color: 'black',
        fontSize: '1.4em',
        marginBlockStart: '0.83em',
        marginBlockEnd: '0.83em',
        lineHeight: '1.5',
    },
    okBtn: {
        fontWeight: '500',
        padding:'10px 30px',
        fontSize:'15px',
        borderRadius: '4px',
        margin:'15px',
        backgroundColor:'#0C3450',
        color:'white',
        cursor:'pointer',
        textTransform: 'capitalize',
        '&:hover': {
            backgroundColor: '#0c3450e0 !important',
        },
    },
    okAlign: {
        display:'flex',
        flexDirection:'row'
    },
    popupBox: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
        borderRadius: '6px',
        padding: '20px 40px',
        width: '420px',
        '@media (max-width:439px)': {
            width: '280px'
        }
    },
    popupText: {
        fontWeight: 'normal',
        textAlign: 'center',
        fontSize: '1.4em',
        marginBlockStart: '0.83em',
        marginBlockEnd: '0.83em',
        lineHeight: '1.5',
    },
    yesButton: {
      border: 0,
        padding: '6px 30px',
        fontSize: '15px',
        margin: '15px',
        color: 'white',
        cursor: 'pointer',
        borderRadius: '4px',
        '&:hover': {
            backgroundColor: '#0c3450e0 !important',
        },
    },
    noButton: {
      border: 0,
        padding: '6px 30px',
        fontSize: '15px',
        margin: '15px',
        color: 'white',
        cursor: 'pointer',
        borderRadius: '4px'
    }
});

  SubmitButton.propTypes = {
    mode: PropTypes.string,
    viewMode: PropTypes.bool,
    hideSubmitButton: PropTypes.bool,
    slug: PropTypes.string,
    submitQuiz: PropTypes.func,
    singleCourseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

export default SubmitButton;
